import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { routeTree } from './routeTree.gen';
import { queryClient } from '@/lib/queryClient';
import { ToastProvider } from '@/lib/toast';
import { ErrorBoundary, FehlerSeite } from '@/components/ui/ErrorBoundary';
import { ReauthDialog } from '@/components/auth/ReauthDialog';
import { UpdateBanner } from '@/components/layout/UpdateBanner';
import { serviceWorkerAnmelden } from '@/lib/swRegistrierung';
import './index.css';

// Der ServiceWorker wird von Hand angemeldet, nicht über den Helfer von
// vite-plugin-pwa — die Begründung steht in lib/swRegistrierung.ts.
// Passend dazu steht `injectRegister: null` in vite.config.ts, sonst
// legte das Plugin eine zweite Anmeldung ins HTML.
//
// Nur im gebauten Stand: im Entwicklungsserver gibt es kein /sw.js, und
// ein Worker, der die Anwendung zwischenspeichert, ist beim Entwickeln
// das Letzte, was man gebrauchen kann.
if (import.meta.env.PROD) {
  serviceWorkerAnmelden();
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Bildlaufposition je Verlaufseintrag merken und beim Zurückgehen
  // wiederherstellen (Befund 29.10.2026). Gescrollt wird nicht im
  // Fenster, sondern in <main> bzw. in der Liste — die tragen dafür
  // `data-scroll-restoration-id`.
  scrollRestoration: true,
  // Faengt Fehler innerhalb des Routers ab, inklusive fehlgeschlagener
  // Search-Param-Validierung (B2).
  defaultErrorComponent: ({ error }) => <FehlerSeite error={error} />
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('root-Element fehlt');
}

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          {/* Faengt abgelaufene Sitzungen ab, ohne den View zu verlassen (S-1).
              Muss ausserhalb des Routers liegen, damit er auf jeder Route
              verfuegbar ist. */}
          <ReauthDialog />
          {/* Meldet eine wartende neue Fassung. Außerhalb des Routers,
              damit der Balken auf jeder Route erscheint. */}
          <UpdateBanner />
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
